import Decimal from 'decimal.js';
import { isEmpty, orderBy } from 'lodash';

import {
  AssetTaxSitusKind,
  AssetV2IntegrationType,
  AssetValuationV2ValuationSource,
  AugmentedCreateAssetStaticInput,
  AugmentedUpdateAssetV2Input,
  IntegrationClientKind,
  StateCode,
  UpdateAssetStaticInput,
} from '@/types/schema';
import { diagnostics } from '@/utils/diagnostics';
import { getNodes } from '@/utils/graphqlUtils';

import {
  StructuredAssetsSubform_AccountFragment,
  StructuredAssetsSubform_AssetFragment,
  StructuredAssetsSubform_ValuationFragment,
} from './graphql/StructuredAssetsSubform.generated';
import {
  AssetTaxSitusFormValue,
  StructuredAsset,
  StructuredAssetsSubformProperties,
  TAX_SITUS_GRANTOR_PREFIX,
  TAX_SITUS_STATE_PREFIX,
} from './StructuredAssetsSubform.types';

// Single-principal entity → that owner's residency; otherwise primary grantor. Returns '' if no primaryClients yet.
export function getOwnerAwareDefaultTaxSitus({
  primaryClients,
  ownerPrincipalIds,
}: {
  primaryClients: { id: string; createdAt?: Date }[] | null | undefined;
  ownerPrincipalIds: string[] | null | undefined;
}): AssetTaxSitusFormValue {
  const sorted = orderBy(primaryClients ?? [], [
    (c) => c.createdAt,
    (c) => c.id,
  ]);
  const primaryGrantor = sorted[0];
  if (!primaryGrantor) return '';

  // Personal asset → single primary-client owner → use that owner's residency.
  if (ownerPrincipalIds && ownerPrincipalIds.length === 1) {
    const ownerId = ownerPrincipalIds[0];
    const ownerIsPrimary = sorted.some((c) => c.id === ownerId);
    if (ownerIsPrimary) {
      return `${TAX_SITUS_GRANTOR_PREFIX}${ownerId}`;
    }
  }

  // Joint / multi-principal / unknown ownership → primary grantor.
  return `${TAX_SITUS_GRANTOR_PREFIX}${primaryGrantor.id}`;
}

export function encodeAssetTaxSitusFormValue(
  assetStatic: StructuredAssetsSubform_AssetFragment['assetStatic']
): AssetTaxSitusFormValue {
  if (!assetStatic?.taxSitusKind) return '';
  switch (assetStatic.taxSitusKind) {
    case AssetTaxSitusKind.GrantorResidency: {
      const grantorId = assetStatic.taxSitusGrantor?.id;
      return grantorId ? `${TAX_SITUS_GRANTOR_PREFIX}${grantorId}` : '';
    }
    case AssetTaxSitusKind.FixedState: {
      const stateCode = assetStatic.taxSitusStateCode;
      return stateCode ? `${TAX_SITUS_STATE_PREFIX}${stateCode}` : '';
    }
    default:
      return '';
  }
}

export function decodeAssetTaxSitusFormValue(
  value: AssetTaxSitusFormValue | undefined
): {
  taxSitusKind?: AssetTaxSitusKind;
  taxSitusStateCode?: StateCode;
  taxSitusGrantorID?: string;
} {
  if (!value) return {};
  if (value.startsWith(TAX_SITUS_GRANTOR_PREFIX)) {
    return {
      taxSitusKind: AssetTaxSitusKind.GrantorResidency,
      taxSitusGrantorID: value.slice(TAX_SITUS_GRANTOR_PREFIX.length),
    };
  }
  if (value.startsWith(TAX_SITUS_STATE_PREFIX)) {
    return {
      taxSitusKind: AssetTaxSitusKind.FixedState,
      taxSitusStateCode: value.slice(
        TAX_SITUS_STATE_PREFIX.length
      ) as StateCode,
    };
  }
  return {};
}

// Coarse situs label suitable for analytics — collapses the encoded form value
// down to one of three buckets so the KPI dashboard can group cleanly.
// NOTE: the non-'unset' labels are pinned to the backend AssetTaxSitusKind
// enum string values, so renaming the enum on the backend will silently flip
// the analytics labels here. If the enum is renamed, update this type and
// `decodeSitusKind` together (and coordinate with the dashboard owner).
export type AssetTaxSitusKindLabel =
  | `${AssetTaxSitusKind}` // 'GRANTOR_RESIDENCY' | 'FIXED_STATE'
  | 'unset';

export function decodeSitusKind(
  value: AssetTaxSitusFormValue | null | undefined
): AssetTaxSitusKindLabel {
  if (!value) return 'unset';
  if (value.startsWith(TAX_SITUS_GRANTOR_PREFIX))
    return AssetTaxSitusKind.GrantorResidency;
  if (value.startsWith(TAX_SITUS_STATE_PREFIX))
    return AssetTaxSitusKind.FixedState;
  return 'unset';
}

// Emits the full set-XOR-clear triplet so the BE coherency hook sees a coherent final state.
export function buildAssetStaticSitusPatch(
  taxSitus: AssetTaxSitusFormValue | undefined
): Pick<
  UpdateAssetStaticInput,
  | 'taxSitusKind'
  | 'taxSitusStateCode'
  | 'taxSitusGrantorID'
  | 'clearTaxSitusKind'
  | 'clearTaxSitusStateCode'
  | 'clearTaxSitusGrantor'
> {
  const situs = decodeAssetTaxSitusFormValue(taxSitus);
  return {
    ...(situs.taxSitusKind
      ? { taxSitusKind: situs.taxSitusKind }
      : { clearTaxSitusKind: true }),
    ...(situs.taxSitusStateCode
      ? { taxSitusStateCode: situs.taxSitusStateCode }
      : { clearTaxSitusStateCode: true }),
    ...(situs.taxSitusGrantorID
      ? { taxSitusGrantorID: situs.taxSitusGrantorID }
      : { clearTaxSitusGrantor: true }),
  };
}

// Returns undefined when the existing AssetStatic should be carried forward (no rename, has assetStaticID).
export function buildWithAssetStaticForCreate(
  asset: StructuredAsset,
  params: { entityID: string; householdID: string }
): AugmentedCreateAssetStaticInput | undefined {
  const renamed =
    !!asset.assetStaticID && asset.title !== asset.assetStaticDisplayName;
  const isNewOrRenamed = !asset.assetStaticID || renamed;
  if (!isNewOrRenamed) return undefined;

  const situs = decodeAssetTaxSitusFormValue(asset.taxSitus);
  return {
    create: {
      name: asset.title,
      entityID: params.entityID,
      householdID: params.householdID,
      ...situs,
    },
  };
}

export function buildUpdateAssetStaticEntry(
  asset: StructuredAsset
): AugmentedUpdateAssetV2Input | null {
  if (!asset.assetID || !asset.assetStaticID) return null;
  const renamed = asset.title !== asset.assetStaticDisplayName;
  if (renamed) return null;
  if (asset.taxSitus === asset._originalTaxSitus) return null;

  return {
    id: asset.assetID,
    update: {},
    updateAssetStatic: {
      id: asset.assetStaticID,
      update: buildAssetStaticSitusPatch(asset.taxSitus),
    },
  };
}

export function getFormAssetsFromAssets(
  assets: StructuredAssetsSubform_AssetFragment[],
  {
    activeClientIntegrationType,
  }: {
    activeClientIntegrationType: IntegrationClientKind | null;
  }
): StructuredAsset[] {
  const assetIntegrationType = activeClientIntegrationType
    ? getAssetIntegrationTypeFromIntegrationClientKind(
        activeClientIntegrationType
      )
    : null;
  return assets.map((asset) => {
    const taxSitus = encodeAssetTaxSitusFormValue(asset.assetStatic);
    return {
      categoryId: asset.class.id,
      title: asset.displayName,
      value: asset.assetValue?.ownedValue ?? new Decimal(0),
      // we pass this specifically for the case where we are editing an integrated valuation
      // and want to add additional manual assets
      integrationType: assetIntegrationType,
      assetID: asset.id,
      assetStaticID: asset.assetStaticID ?? null,
      assetStaticDisplayName: asset.assetStaticID ? asset.displayName : null,
      taxSitus,
      _originalTaxSitus: taxSitus,
    };
  });
}

function getFormValuesFromValuation(
  accountId: string,
  valuation: StructuredAssetsSubform_ValuationFragment | null
): StructuredAssetsSubformProperties {
  let dateOfValuation = valuation?.effectiveDate ?? null;
  // If a valuation is present, but the date is the epoch, then the valuation
  // is a placeholder and should be treated as if it doesn't exist in the form.
  if (
    dateOfValuation?.getTime() === new Date(new Date(0).toISOString()).getTime()
  ) {
    dateOfValuation = null;
  }

  const assets = (() => {
    // if we're looking at an automated valuation, we only want to load assets into the structured assets subform
    // that are NOT being pulled from an integration. This is because we want to allow the user to add additional
    // manual assets via the subform.
    if (valuation?.valuationSource !== AssetValuationV2ValuationSource.Manual) {
      return getManualAssetsFromIntegratedValuation(valuation);
    }
    return getNodes(valuation?.assets);
  })();
  const formAssets = getFormAssetsFromAssets(assets, {
    activeClientIntegrationType: null,
  });
  const assetsValueSum = getAssetValueSum(formAssets);

  return {
    accountId,
    valuationId: valuation?.id,
    documentIds: [],
    description: valuation?.description ?? '',
    assets: formAssets,
    _assetsValueSum: assetsValueSum,
  };
}

export function getAssetValueSum(
  assets: { value?: Decimal | null }[] | null | undefined
): Decimal {
  return (assets || []).reduce((acc, a) => {
    acc = acc.add(a.value ?? new Decimal(0));
    return acc;
  }, new Decimal(0));
}

// A row is "empty" when the user hasn't given it a name or a value. We
// intentionally ignore categoryId and taxSitus because both are auto-filled
// with defaults on a fresh row and would otherwise make a blank row look real.
// A row that maps to a persisted asset (assetID/assetStaticID) or an
// integration-sourced asset is never considered empty.
export function isEmptyStructuredAssetRow(
  asset: Partial<StructuredAsset> | null | undefined
): boolean {
  if (!asset) return true;

  const hasPersistedIdentity =
    Boolean(asset.assetID) ||
    Boolean(asset.assetStaticID) ||
    Boolean(asset.integrationType);
  if (hasPersistedIdentity) return false;

  const titleEmpty = !asset.title?.trim();
  const valueEmpty = asset.value == null || asset.value.isZero();

  return titleEmpty && valueEmpty;
}

// Submitting a single empty row is allowed and means "no assets of this kind",
// so the row's fields shouldn't be required in that case. With more than one
// row, every row is required (the user must fill it in or delete it).
export function shouldRequireStructuredAssetFields(
  assets: Partial<StructuredAsset>[] | null | undefined
): boolean {
  return !(assets?.length === 1 && isEmptyStructuredAssetRow(assets[0]));
}

// Drops empty rows at serialization time so an empty sole row submits as
// "no assets" rather than creating a junk asset.
export function filterEmptyStructuredAssetRows<
  T extends Partial<StructuredAsset>,
>(assets: T[] | null | undefined): T[] {
  return (assets ?? []).filter((asset) => !isEmptyStructuredAssetRow(asset));
}

export function getStructuredAssetsSubformValues(
  designerAccount: StructuredAssetsSubform_AccountFragment | null
): StructuredAssetsSubformProperties {
  const emptyFormData: StructuredAssetsSubformProperties = {
    accountId: undefined,
    valuationId: undefined,
    description: '',
    documentIds: [],
    assets: [],
    _assetsValueSum: new Decimal(0),
  };

  if (!designerAccount) {
    diagnostics.error('No designer account found');
    return emptyFormData;
  }

  const mostRecentValuation =
    designerAccount.valuations.edges?.[0]?.node ?? null;
  const formValues = getFormValuesFromValuation(
    designerAccount.id,
    mostRecentValuation
  );
  return formValues;
}

/**
 * Returns true if the valuation has additional manual assets on top of an integration,
 * meaning assets that are not linked to an integration. Returns false if there is no valuation,
 * or if the valuation is manual.
 */
export function valuationHasAdditionalManualAssets(
  valuation: StructuredAssetsSubform_ValuationFragment | null
) {
  const assets = getManualAssetsFromIntegratedValuation(valuation);
  return !isEmpty(assets);
}

function getManualAssetsFromIntegratedValuation(
  valuation: StructuredAssetsSubform_ValuationFragment | null
) {
  if (valuation?.valuationSource === AssetValuationV2ValuationSource.Manual) {
    return [];
  }
  return getNodes(valuation?.assets).filter((asset) => !asset.integrationType);
}

export function getIntegratedAssetsFromIntegratedValuation(
  valuation: StructuredAssetsSubform_ValuationFragment | null
): StructuredAssetsSubform_AssetFragment[] {
  const assets = getNodes(valuation?.assets).filter(
    (asset) => asset.integrationType
  );

  return assets;
}

export function getAssetIntegrationTypeFromIntegrationClientKind(
  integrationClientKind: IntegrationClientKind
): AssetV2IntegrationType | null {
  switch (integrationClientKind) {
    case IntegrationClientKind.Addepar:
      return AssetV2IntegrationType.Addepar;
    case IntegrationClientKind.BlackDiamond:
      return AssetV2IntegrationType.BlackDiamond;
    case IntegrationClientKind.Orion:
      return AssetV2IntegrationType.Orion;
    case IntegrationClientKind.Emoney:
      return AssetV2IntegrationType.Emoney;
    default:
      return null;
  }
}
